import { useCallback, useEffect } from 'react';
import type { DropTarget } from '../types';
import { findDropTargetUnified } from '../types';
import { useCanvasStores } from '../stores/CanvasContext';
import { useScreenToCanvas } from './useScreenToCanvas';

export interface DragEffectConfig<T> {
  /** Ref that holds drag state (null = not dragging) */
  dragRef: React.RefObject<T | null>;
  /** Check if the ref indicates an active drag */
  isActive: (ref: T) => boolean;
  /** Called on pointermove — should return the exclude entityId for findDropTarget */
  getExcludeEntityId: (drag: T) => string;
  /** Called on pointermove — update drag ref & dispatch to store */
  onMove: (canvasPoint: { x: number; y: number }, drag: T, target: DropTarget | null) => void;
  /** Called on pointerup — dispatch store event */
  onDrop: (canvasPoint: { x: number; y: number }, drag: T, target: DropTarget | null) => void;
  /** Called to reset the drag — clear ref & dispatch cleanup */
  onCleanup: () => void;
}

/**
 * Generic hook for pointer-based drag effects (connection drag, edge redock).
 * Registers pointermove/pointerup/keydown on document and handles the shared
 * event-listener lifecycle.
 */
export function useDragEffect<T>(config: DragEffectConfig<T>): void {
  const { store } = useCanvasStores();

  const screenToCanvas = useScreenToCanvas();

  const findTarget = useCallback((canvasPoint: { x: number; y: number }, excludeEntityId: string): DropTarget | null => {
    const ctx = store.getSnapshot().context;
    const entities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
      ...Object.values(ctx.groups),
    ];
    return findDropTargetUnified(canvasPoint, entities, excludeEntityId);
  }, [store]);

  useEffect(() => {
    const { dragRef, isActive, getExcludeEntityId, onMove, onDrop, onCleanup } = config;

    function handlePointerMove(e: PointerEvent) {
      const drag = dragRef.current;
      if (!drag || !isActive(drag)) return;
      const canvasPoint = screenToCanvas(e.clientX, e.clientY);
      const target = findTarget(canvasPoint, getExcludeEntityId(drag));
      onMove(canvasPoint, drag, target);
    }

    function handlePointerUp(e: PointerEvent) {
      const drag = dragRef.current;
      if (!drag || !isActive(drag)) return;
      const canvasPoint = screenToCanvas(e.clientX, e.clientY);
      const target = findTarget(canvasPoint, getExcludeEntityId(drag));
      onDrop(canvasPoint, drag, target);
      onCleanup();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && dragRef.current) {
        onCleanup();
      }
    }

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [screenToCanvas, findTarget, config]);
}
